"use client";

import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, ChevronDown, Close, Search } from "@/components/ui/icons";
import { BLOG, BLOG_POSTS, postProduct, postUrl, type BlogPost } from "@/content/blog";
import { PRODUCTS, type Product } from "@/content/quikit";
import { useLenis } from "@/hooks/useLenis";
import { cn } from "@/lib/utils";
import { PostCover } from "./PostCover";

const EASE = [0.23, 1, 0.32, 1] as const;
/** A product key from the catalogue, or every article. */
type Filter = string | "All";

/** Articles per product. A product with none still appears in the menu, showing 0. */
const COUNTS = BLOG_POSTS.reduce<Record<string, number>>((acc, post) => {
  const key = postProduct(post.slug);
  if (key) acc[key] = (acc[key] ?? 0) + 1;
  return acc;
}, {});

/**
 * Every product, grouped by family. PRODUCTS is ordered with each family contiguous, so
 * folding adjacent entries keeps the menu in the same order as the nav.
 */
const GROUPS = PRODUCTS.reduce<{ family: string; apps: Product[] }[]>((acc, p) => {
  const last = acc[acc.length - 1];
  if (last?.family === p.family) last.apps.push(p);
  else acc.push({ family: p.family, apps: [p] });
  return acc;
}, []);

const productName = (key: string) => PRODUCTS.find((p) => p.key === key)?.name ?? key;
const filterLabel = (f: Filter) => (f === "All" ? "All products" : productName(f));

function matches(p: BlogPost, q: string) {
  if (!q) return true;
  const product = postProduct(p.slug);
  return `${p.title} ${p.excerpt} ${p.category} ${product ? productName(product) : ""}`.toLowerCase().includes(q);
}

/** One row of the product menu. Products without an article yet are listed but inert. */
function ProductOption({
  value,
  label,
  selected,
  count,
  onPick,
}: {
  value: Filter;
  label?: string;
  selected: boolean;
  count: number;
  onPick: (f: Filter) => void;
}) {
  return (
    <li role="option" aria-selected={selected}>
      <button
        type="button"
        disabled={count === 0}
        onClick={() => onPick(value)}
        className={cn(
          "flex w-full items-center justify-between gap-3 rounded-[12px] px-3 py-2 text-left text-[14px] transition-colors",
          selected ? "bg-panel font-medium" : "hover:bg-panel",
          count === 0 && "pointer-events-none text-faint",
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{label ?? "All products"}</span>
          {selected && <Check className="size-3.5 shrink-0 text-accent" strokeWidth={2.4} />}
        </span>
        <span className="text-[12px] tabular-nums text-faint">{count}</span>
      </button>
    </li>
  );
}

/**
 * Product picker that sits beside the search field. Every app in the catalogue is listed under
 * its family, each with how many articles it has; the ones with none are shown but not
 * selectable, so the menu is a full index of the suite without any dead ends.
 */
function ProductMenu({ value, onChange }: { value: Filter; onChange: (f: Filter) => void }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (f: Filter) => {
    onChange(f);
    setOpen(false);
    trigger.current?.focus();
  };

  return (
    <div ref={root} className="relative shrink-0">
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-12 w-full items-center justify-between gap-2 rounded-full bg-panel px-5 text-[14px] font-medium outline-none ring-1 ring-inset ring-transparent transition-shadow focus-visible:ring-2 focus-visible:ring-accent sm:w-[210px]"
      >
        <span className="truncate">{filterLabel(value)}</span>
        <ChevronDown className={cn("size-3.5 shrink-0 text-muted transition-transform duration-300", open && "rotate-180")} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            aria-label="Filter by product"
            // Lenis drives the page from the wheel, so an inner scroller has to opt out of it.
            data-lenis-prevent=""
            initial={{ opacity: 0, y: -6, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -6, filter: "blur(4px)" }}
            transition={{ duration: 0.22, ease: EASE }}
            className="no-scrollbar absolute right-0 top-[calc(100%+8px)] z-20 max-h-[360px] w-full overflow-y-auto overscroll-contain rounded-[18px] bg-white p-1.5 shadow-[0_18px_48px_rgba(10,10,11,0.12)] ring-1 ring-inset ring-ink/[0.07] sm:w-[268px]"
          >
            <ProductOption value="All" selected={value === "All"} count={BLOG_POSTS.length} onPick={pick} />

            {GROUPS.map((g) => (
              <li key={g.family}>
                <p className="px-3 pb-1 pt-3 text-[10.5px] font-medium uppercase tracking-[0.1em] text-faint">{g.family}</p>
                <ul>
                  {g.apps.map((app) => (
                    <ProductOption
                      key={app.key}
                      value={app.key}
                      label={app.name}
                      selected={value === app.key}
                      count={COUNTS[app.key] ?? 0}
                      onPick={pick}
                    />
                  ))}
                </ul>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

function PostCard({ post, index }: { post: BlogPost; index: number }) {
  return (
    <motion.a
      href={postUrl(post.slug)}
      initial={{ opacity: 0, y: 48, filter: "blur(8px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: EASE, delay: (index % 3) * 0.08 }}
      className="group flex flex-col overflow-hidden rounded-[24px] bg-panel transition-[scale] duration-200 active:scale-[0.99]"
    >
      <div className="relative m-2 aspect-[16/10] overflow-hidden rounded-[18px]">
        <PostCover slug={post.slug} className="absolute inset-0" />
        <span className="absolute left-3 top-3 rounded-full bg-white/85 px-2.5 py-1 text-[10.5px] font-medium uppercase tracking-[0.08em] text-ink backdrop-blur">
          {post.category}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-5 pt-3">
        <p className="text-[11.5px] text-muted">{post.date}</p>
        <h3 className="mt-2 line-clamp-3 text-[16px] font-medium leading-[1.35] tracking-[-0.02em]">{post.title}</h3>
        <p className="mt-2 line-clamp-3 text-[14px] leading-[1.6] text-muted">{post.excerpt}</p>
        <span className="mt-auto flex items-center gap-1 pt-5 text-[14px] font-medium">
          Read article
          <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </span>
      </div>
    </motion.a>
  );
}

/**
 * All articles: search, a product filter (with counts) and pagination. Changing a filter or
 * page swaps the grid with a soft blur cross-fade and brings the list back into view.
 */
export function BlogIndex() {
  const lenis = useLenis();
  const top = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState<Filter>("All");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const q = useDeferredValue(query.trim().toLowerCase());

  // Product pages link here as `/blog?product=crm`, so the list opens on that product.
  // Read after mount rather than during render: the page is static, and the server has no
  // query string to match against. Unknown or empty keys simply leave the filter on "All".
  useEffect(() => {
    const key = new URLSearchParams(window.location.search).get("product");
    if (key && PRODUCTS.some((x) => x.key === key)) setFilter(key);
  }, []);

  const results = useMemo(
    () => BLOG_POSTS.filter((p) => (filter === "All" || postProduct(p.slug) === filter) && matches(p, q)),
    [filter, q],
  );
  const pages = Math.max(1, Math.ceil(results.length / BLOG.perPage));
  const current = Math.min(page, pages);
  const visible = results.slice((current - 1) * BLOG.perPage, current * BLOG.perPage);

  const goTo = (n: number) => {
    setPage(n);
    if (!top.current) return;
    if (lenis) lenis.scrollTo(top.current, { offset: -110 });
    else top.current.scrollIntoView();
  };
  const reset = () => {
    setFilter("All");
    setQuery("");
    setPage(1);
  };

  return (
    <section id="articles" aria-label="All articles" className="scroll-mt-28 pt-[clamp(48px,5vw,80px)]">
      <div className="wrap">
        {/* The hero above already introduces the blog, so the list opens straight on its controls. */}
        {/* Full column width so the controls line up with the card grid below. */}
        <div ref={top} className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
          {/* A div, not a wrapping label: the Clear button inside would otherwise become part of the
              input's accessible name ("Search articles Clear search"). */}
          <div className="relative block w-full flex-1">
            <label htmlFor="blog-search" className="sr-only">
              Search articles
            </label>
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted" />
            <input
              id="blog-search"
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search articles"
              className="h-12 w-full rounded-full bg-panel pl-11 pr-11 text-[14px] outline-none ring-1 ring-inset ring-transparent transition-shadow placeholder:text-faint focus:ring-2 focus:ring-accent [&::-webkit-search-cancel-button]:hidden"
            />
            {query && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-full bg-white text-muted transition-colors hover:text-ink"
              >
                <Close className="size-3.5" />
              </button>
            )}
          </div>

          <ProductMenu
            value={filter}
            onChange={(f) => {
              setFilter(f);
              setPage(1);
            }}
          />
        </div>

        <p aria-live="polite" className="mt-5 text-[14px] text-muted">
          {results.length === 0
            ? "No articles found"
            : `Showing ${(current - 1) * BLOG.perPage + 1}–${(current - 1) * BLOG.perPage + visible.length} of ${results.length} ${results.length === 1 ? "article" : "articles"}`}
          {q && ` for “${query.trim()}”`}
        </p>

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`${filter}|${q}|${current}`}
            initial={{ opacity: 0, filter: "blur(6px)" }}
            animate={{ opacity: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, filter: "blur(6px)" }}
            transition={{ duration: 0.3, ease: EASE }}
          >
            {visible.length ? (
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-3">
                {visible.map((post, i) => (
                  <PostCard key={post.slug} post={post} index={i} />
                ))}
              </div>
            ) : (
              <div className="mt-4 flex flex-col items-center rounded-[24px] bg-panel px-6 py-16 text-center">
                <span className="grid size-12 place-items-center rounded-full bg-white">
                  <Search className="size-5 text-muted" />
                </span>
                <h3 className="mt-5 text-[20px] font-[450] tracking-[-0.03em]">Nothing matches that yet</h3>
                <p className="mt-2 max-w-[36ch] text-[14px] text-muted">Try another keyword or product — new guides are added every month.</p>
                <button type="button" onClick={reset} className="btn btn-mint mt-6 h-10 px-5 text-[14px]">
                  <span className="btn-label" data-btn-label="">
                    <span data-text="Clear filters">Clear filters</span>
                  </span>
                </button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {pages > 1 && (
          <nav aria-label="Pagination" className="mt-10 flex items-center justify-center gap-1.5">
            <button
              type="button"
              onClick={() => goTo(current - 1)}
              disabled={current === 1}
              className="flex h-10 items-center gap-1.5 rounded-full px-4 text-[14px] font-medium transition-colors hover:bg-panel disabled:pointer-events-none disabled:opacity-35"
            >
              <ArrowRight className="size-3.5 rotate-180" />
              Prev
            </button>
            {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => goTo(n)}
                aria-current={n === current ? "page" : undefined}
                className={cn(
                  "grid size-10 place-items-center rounded-full text-[14px] font-medium tabular-nums transition-colors",
                  n === current ? "bg-ink text-white" : "hover:bg-panel",
                )}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              onClick={() => goTo(current + 1)}
              disabled={current === pages}
              className="flex h-10 items-center gap-1.5 rounded-full px-4 text-[14px] font-medium transition-colors hover:bg-panel disabled:pointer-events-none disabled:opacity-35"
            >
              Next
              <ArrowRight className="size-3.5" />
            </button>
          </nav>
        )}
      </div>
    </section>
  );
}
